import { useEffect } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import type { GalleryImage } from "../../../types/gallery";

interface ImageModalProps {
  image: GalleryImage | null;
  images: GalleryImage[];
  currentIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

const ImageModal: React.FC<ImageModalProps> = ({
  image,
  images,
  currentIndex,
  onClose,
  onNavigate,
}) => {
  useEffect(() => {
    if (!image) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }

      if (e.key === "ArrowLeft" && currentIndex > 0) {
        onNavigate(currentIndex - 1);
      }

      if (e.key === "ArrowRight" && currentIndex < images.length - 1) {
        onNavigate(currentIndex + 1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [image, currentIndex, images.length, onClose, onNavigate]);

  if (!image) return null;

  const isFirst = currentIndex === 0;
  const isLast = currentIndex === images.length - 1;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="relative max-w-5xl w-full flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 text-white hover:text-red-400 transition"
        >
          <X size={32} />
        </button>

        {/* Left Arrow */}
        {!isFirst && (
          <button
            onClick={() => onNavigate(currentIndex - 1)}
            className="absolute left-2 md:-left-16 top-1/2 -translate-y-1/2
                       bg-black/50 hover:bg-black/80 text-white
                       rounded-full p-2 md:p-3 transition z-10"
            aria-label="Previous image"
          >
            <ChevronLeft size={32} />
          </button>
        )}

        {/* Image */}
        <img
          src={image.url}
          alt={image.name}
          className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
        />

        {/* Right Arrow */}
        {!isLast && (
          <button
            onClick={() => onNavigate(currentIndex + 1)}
            className="absolute right-2 md:-right-16 top-1/2 -translate-y-1/2
                       bg-black/50 hover:bg-black/80 text-white
                       rounded-full p-2 md:p-3 transition z-10"
            aria-label="Next image"
          >
            <ChevronRight size={32} />
          </button>
        )}

        {/* Image Counter */}
        <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 text-white text-sm">
          {currentIndex + 1} / {images.length}
        </div>
      </div>
    </div>
  );
};

export default ImageModal;
